'use client';

// Hook dịch ĐỘNG dữ liệu tenant trên /sale — xem lib/sale-i18n-server.ts (cache vĩnh viễn ở Firestore).
// Client: gom mọi text cần dịch trong cùng 1 nhịp render thành 1 request (debounce 60ms), nhớ kết quả
// trong phiên (Map) nên đổi qua lại giữa các căn không hỏi lại. Chưa có bản dịch → hiện nguyên văn
// (tiếng Việt/Anh gốc của tenant) rồi tự đổi sang bản dịch khi về; lỗi/hết ngân sách → giữ nguyên văn.
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useSaleLang, type SaleLang } from './sale-i18n';
import { isSaleTrLang, needsSaleTranslation, SALE_TR_MAX_TEXTS, SALE_TR_MAX_TEXT_LEN } from './sale-tr-shared';
import { logPublicClientError } from './client-error-logger';

const mem = new Map<string, string>();     // `${lang}\u0001${nguyên văn}` → bản dịch
const asked = new Set<string>();           // đã xếp hàng/gửi (kể cả thất bại) — KHÔNG hỏi lại trong phiên, tránh vòng lặp tốn token
const queue = new Map<SaleLang, Set<string>>();
let timer: ReturnType<typeof setTimeout> | null = null;
let version = 0;
const listeners = new Set<() => void>();
const memKey = (lang: SaleLang, text: string) => `${lang}\u0001${text}`;
const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };
const bump = () => { version++; listeners.forEach(fn => fn()); };

async function sendBatch(lang: SaleLang, texts: string[]) {
  try {
    const res = await fetch('/api/sale/translate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang, texts }),
    });
    if (!res.ok) throw new Error(`translate ${res.status}`);
    const data = (await res.json()) as { translations?: Record<string, string> };
    for (const [src, tr] of Object.entries(data.translations ?? {})) {
      if (typeof tr === 'string' && tr) mem.set(memKey(lang, src), tr);
    }
    bump();
  } catch (e) {
    // Dịch chỉ là phần phụ — khách vẫn xem được nguyên văn; nhưng lỗi kéo dài = mất tính năng âm thầm ⇒ ghi lại.
    logPublicClientError({ route: 'lib/sale-i18n-dyn.ts', action: 'translate_batch', message: e instanceof Error ? e.message : String(e), details: { lang, n: texts.length } });
  }
}

function flush() {
  timer = null;
  for (const [lang, set] of queue) {
    const all = [...set]; set.clear();
    for (let i = 0; i < all.length; i += SALE_TR_MAX_TEXTS) void sendBatch(lang, all.slice(i, i + SALE_TR_MAX_TEXTS));
  }
}

function enqueue(lang: SaleLang, texts: string[]) {
  if (!isSaleTrLang(lang)) return;
  let added = false;
  for (const raw of texts) {
    const text = raw.length > SALE_TR_MAX_TEXT_LEN ? raw.slice(0, SALE_TR_MAX_TEXT_LEN) : raw;
    const k = memKey(lang, text);
    if (mem.has(k) || asked.has(k) || !needsSaleTranslation(text, lang)) continue;
    asked.add(k);
    (queue.get(lang) ?? queue.set(lang, new Set()).get(lang)!).add(text);
    added = true;
  }
  if (added && !timer) timer = setTimeout(flush, 60);
}

/**
 * `const tr = useSaleTr([a, b, …])` → `tr(text)` trả bản dịch sang ngôn ngữ trang (hoặc nguyên văn nếu là
 * tiếng Việt / chưa có / không cần dịch). Truyền ĐỦ mọi text sẽ gọi `tr()` trong component vào mảng để
 * được dịch cùng 1 lô.
 */
export function useSaleTr(texts: ReadonlyArray<string | null | undefined>): (s: string) => string {
  const { lang } = useSaleLang();
  const ver = useSyncExternalStore(subscribe, () => version, () => 0);
  const key = texts.filter((t): t is string => !!t).join('\u0002');
  useEffect(() => {
    if (lang === 'vi' || !key) return;
    enqueue(lang, key.split('\u0002'));
  }, [lang, key]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `ver` đổi khi có bản dịch mới về → cần callback mới để re-render
  return useCallback((s: string) => (lang === 'vi' ? s : (mem.get(memKey(lang, s.length > SALE_TR_MAX_TEXT_LEN ? s.slice(0, SALE_TR_MAX_TEXT_LEN) : s)) ?? s)), [lang, ver]);
}
